import React from "react"

import { useTranslation } from "react-i18next"

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import BlockIcon from "@mui/icons-material/Block"
import CachedIcon from "@mui/icons-material/Cached"
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined"
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined"
import CodeIcon from "@mui/icons-material/Code"
import FilterListIcon from "@mui/icons-material/FilterList"
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined"
import SubjectIcon from "@mui/icons-material/Subject"
import TagIcon from "@mui/icons-material/Tag"
import TokenOutlinedIcon from "@mui/icons-material/TokenOutlined"
import { Card, Grid } from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"

import type { NodeDataBasedOnType } from "../../../features/Workflows/types/interfaces"
import { getTheme } from "../../../hooks/UseTheme"

import styles from "../../../features/Workflows/components/nodes/Nodes.module.scss"

interface Props {
  nodeType:
    | "scorecardset"
    | "ruleset"
    | "taglist"
    | "program"
    | "label"
    | "filter"
    | "calculator"
    | "loop-start"
    | "loop-end"
    | "project"
    | "script"
    | "AddBlockNode"

  nodeBody?: React.ReactElement
}

/**
 * Retrieves node data based on the node type. - used in (simulation sankey chart and loops explaination block)
 *
 * @param nodeType - The type of the node.
 * @param nodeBody - the react element to render (node body)
 * @returns {React.ReactElement} The node data including logo, title, and logo class name.
 */
export function LabelAuditCard(props: Readonly<Props>): React.ReactElement {
  const { nodeType, nodeBody } = props

  const theme = getTheme()
  const { t } = useTranslation("cards")

  const getNodesDataBasedOnNodeType = (
    nodeType:
      | "scorecardset"
      | "ruleset"
      | "taglist"
      | "program"
      | "label"
      | "filter"
      | "calculator"
      | "loop-start"
      | "loop-end"
      | "project"
      | "script"
      | "AddBlockNode",
  ): NodeDataBasedOnType => {
    switch (nodeType) {
      case "ruleset":
        return {
          nodeLogo: <RuleOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.ruleset"),
          logoClassName: "RulesetCardType",
        }
      case "scorecardset":
        return {
          nodeLogo: <AssignmentOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.scorecardset"),
          logoClassName: "ScorecardsetCardType",
        }
      case "program":
        return {
          nodeLogo: <SubjectIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.program"),
          logoClassName: "ProgramCardType",
        }
      case "filter":
        return {
          nodeLogo: <FilterListIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.filter"),
          logoClassName: "filterCardType",
        }
      case "calculator":
        return {
          nodeLogo: <CalculateOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.calculator"),
          logoClassName: "calculatorCardType",
        }
      case "project":
        return {
          nodeLogo: <TokenOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.project"),
          logoClassName: "ProjectCardType",
        }
      case "taglist":
        return {
          nodeLogo: <TagIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.taglist"),
          logoClassName: "TaglistCardType",
        }
      case "script":
        return {
          nodeLogo: <CodeIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.script"),
          logoClassName: "ScriptCardType",
        }
      case "loop-start":
        return {
          nodeLogo: <CachedIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.loopStart"),
          logoClassName: "loopStartCardType",
        }
      case "loop-end":
        return {
          nodeLogo: <CheckOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.loopEnd"),
          logoClassName: "loopEndCardType",
        }
      default:
        return {
          nodeLogo: <BlockIcon sx={{ color: theme.palette.base.text[1] }} />,
          nodeTitle: t("labelAuditCard.nodeTitles.label"),
          logoClassName: "endCardType",
        }
    }
  }

  return (
    <Card className={styles.card} style={{ width: "240px", marginRight: "0px" }}>
      <Grid container>
        {/* Node Logo */}
        <Grid item width={"40px"}>
          <Grid item className={styles[`${getNodesDataBasedOnNodeType(nodeType)?.logoClassName}`]}>
            {getNodesDataBasedOnNodeType(nodeType)?.nodeLogo}
          </Grid>
        </Grid>

        <Grid
          item
          xs
          flexDirection={"column"}
          display="flex"
          paddingLeft={1}
          justifyContent={nodeType === "loop-end" ? "center" : "space-between"}
          alignItems={"flex-start"}
          position={"relative"}
          overflow={"hidden"}
        >
          <Grid item container justifyContent="space-between">
            <Typography variant="label-bold" variantColor={2}>
              {getNodesDataBasedOnNodeType(nodeType)?.nodeTitle}
            </Typography>
          </Grid>

          {/* Node Body*/}
          {nodeType !== "loop-end" && nodeBody}
        </Grid>
      </Grid>
    </Card>
  )
}
